import { useCallback, useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { formatMoney } from "@/shared/format";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { BottoniEsito } from "./BottoniEsito";
import { leggiDaDecidere, nomeEsito, salvaEsito, type DaDecidere, type Esito } from "./esitiApi";

const giorno = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString("it-IT") : "";

/**
 * «Resi senza esito» (Davide 03/11/2026): gli ordini partiti e tornati (o rimborsati)
 * di cui non sappiamo che fine ha fatto la merce. Finché non lo diciamo la BI la conta
 * persa. OM propone una risposta dai segni che ha già e la spiega; un clic la salva e la
 * riga sparisce.
 */
export function EsitiDaDecidere() {
  const [righe, setRighe] = useState<DaDecidere[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [occupato, setOccupato] = useState<number | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);

  const carica = useCallback(() => {
    leggiDaDecidere()
      .then((r) => {
        setRighe(Array.isArray(r) ? r : []);
        setErrore(null);
      })
      .catch((e: unknown) => setErrore(prettifyApiError(e)));
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  async function scegli(riga: DaDecidere, esito: Esito) {
    setOccupato(riga.order_id);
    setErrore(null);
    try {
      await salvaEsito({ orderId: riga.order_id }, esito);
      // La riga sparisce subito: l'elenco vero lo conferma al prossimo caricamento.
      setRighe((prima) => (prima ?? []).filter((r) => r.order_id !== riga.order_id));
      setFatto(`Salvato: ordine ${riga.reference} · ${nomeEsito(esito)}.`);
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      setOccupato(null);
    }
  }

  return (
    <section aria-labelledby="esiti-titolo" className="mb-5">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 id="esiti-titolo" className="font-display text-[16px] font-bold text-ink">
          Resi senza esito
        </h2>
        {righe && righe.length > 0 && (
          <span className="text-[12.5px] font-bold tabular-nums text-ink">{righe.length}</span>
        )}
      </div>
      <p className="mb-3 text-[13px] text-ink">
        Dì che fine fa la merce: la BI la conta persa finché non lo sappiamo.
      </p>

      <ErrorBanner message={errore} className="mb-3" />
      {fatto && (
        <p
          role="status"
          className="mb-3 rounded-md border border-green/30 bg-green-soft px-3 py-2 text-[13px]"
        >
          {fatto}
        </p>
      )}

      {righe === null && !errore ? (
        <div aria-busy="true" className="h-16 rounded-md bg-paper-2 motion-safe:animate-pulse" />
      ) : righe !== null && righe.length === 0 ? (
        <Card className="py-5 text-center text-[13px] text-ink">Tutti i resi hanno un esito</Card>
      ) : (
        <ul className="flex flex-col gap-[10px]">
          {(righe ?? []).map((r) => (
            <li key={r.order_id}>
              <Card className="flex flex-col gap-2.5">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="break-all font-mono text-[13.5px] font-bold text-ink">
                    {r.reference}
                  </span>
                  <span className="text-[12.5px] text-ink">
                    {[r.channel, giorno(r.data)].filter(Boolean).join(" · ")}
                  </span>
                  <span className="ml-auto text-[13px] font-semibold tabular-nums text-ink">
                    {r.costo == null
                      ? "Costo non noto"
                      : `Merce ${formatMoney(Number(r.costo), "EUR")}`}
                  </span>
                </div>
                {r.proposta ? (
                  <p className="rounded-md border border-emerald-700/30 bg-green-soft px-3 py-2 text-[13px] text-ink">
                    Propongo: <strong>{nomeEsito(r.proposta)}</strong>. {r.perche}
                  </p>
                ) : (
                  <p className="text-[13px] text-ink">Nessun segno: scegli tu.</p>
                )}
                <BottoniEsito
                  proposta={r.proposta}
                  disabled={occupato === r.order_id}
                  ariaPrefisso={`Ordine ${r.reference}`}
                  onScegli={(e) => void scegli(r, e)}
                />
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
